import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["card", "barName", "barPrice", "bookButton", "preview", "providerCard", "providerCheck", "topReviews"];

  connect() {
    if (this.hasPreviewTarget) {
      this.previewTargets.forEach(el => el.classList.add("hidden"));
    }
    
    if (this.cardTargets.length > 0) {
      this.activateService(this.cardTargets[0], { expand: false, scroll: false });
    }
  }

  select(event) {
    if (event.type === "keydown") event.preventDefault();
    this.activateService(event.currentTarget, { expand: true, scroll: true });
  }

  activateService(card, { expand, scroll }) {
    this.selectService(card);
    
    const index = this.cardTargets.indexOf(card);
    if (this.hasPreviewTarget) {
      this.previewTargets.forEach((el, i) => {
        const shouldShow = expand && i === index && el.dataset.empty !== "true";
        el.classList.toggle("hidden", !shouldShow);
      });
      if (this.previewTargets[index]) {
        const preview = this.previewTargets[index];
        
        const providers = preview.querySelectorAll('.provider-card');
        if (providers.length > 0) {
          const hasActive = Array.from(providers).some(p => p.classList.contains("active"));
          if (!hasActive) {
            this.selectProviderEle(providers[0]);
          } else {
             const activeProv = Array.from(providers).find(p => p.classList.contains("active"));
             if (activeProv) {
               this.updateBookingUrl(activeProv.dataset.serviceId, activeProv.dataset.providerId, card.dataset.siteSlug);
             }
          }
        } else if (card.dataset.providerId) {
          this.updateBookingUrl(card.dataset.serviceId, card.dataset.providerId, card.dataset.siteSlug);
        } else {
          this.updateBookingUrl(card.dataset.serviceId, null, card.dataset.siteSlug);
        }
        
        if (expand && scroll && preview.dataset.empty !== "true") {
          preview.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    }

    this.refreshTopReviews(card.dataset.siteSlug, card.dataset.serviceId);
  }

  selectService(card) {
    this.cardTargets.forEach(c => {
      c.classList.remove("active");
      c.setAttribute("aria-pressed", "false");
    });
    card.classList.add("active");
    card.setAttribute("aria-pressed", "true");

    // Update booking bar UI texts
    const name = card.dataset.serviceName;
    const priceText = card.dataset.servicePriceText;
    
    if (this.hasBarNameTarget) this.barNameTarget.textContent = name;
    if (this.hasBarPriceTarget) this.barPriceTarget.textContent = priceText;
    
    this.updateBookingUrl(card.dataset.serviceId, card.dataset.providerId || null, card.dataset.siteSlug);
  }

  selectProvider(event) {
    event.stopPropagation(); // Prevent bubbling up to the service card which would close/open it
    const providerCard = event.currentTarget;
    this.selectProviderEle(providerCard);
  }

  selectProviderEle(providerCard) {
    const serviceId = providerCard.dataset.serviceId;
    const providerId = providerCard.dataset.providerId;

    // Constrain the DOM search to just this service's preview element
    const preview = providerCard.closest('[data-site-services-target="preview"]');
    if (!preview) return;

    // Remove active class and hide checks from all providers inside this preview
    const allProviders = preview.querySelectorAll('.provider-card');
    allProviders.forEach(p => {
      p.classList.remove("active");
      const check = p.querySelector('[data-site-services-target="providerCheck"]');
      if (check) {
        check.classList.add("hidden", "scale-0");
        check.classList.remove("flex", "scale-100");
      }
    });

    // Add active class and show check for selected provider
    providerCard.classList.add("active");
    const check = providerCard.querySelector('[data-site-services-target="providerCheck"]');
    if (check) {
      check.classList.remove("hidden", "scale-0");
      check.classList.add("flex", "scale-100");
    }

    // Figure out the site slug using the parent service card
    const mainCard = this.cardTargets.find(c => c.dataset.serviceId === serviceId);
    if (mainCard) {
      this.updateBookingUrl(serviceId, providerId, mainCard.dataset.siteSlug);
    }
  }

  updateBookingUrl(serviceId, providerId, siteSlug) {
    if (this.hasBookButtonTarget && siteSlug) {
      let url = `/${siteSlug}/appointments/new?service_id=${serviceId}`;
      if (providerId) {
        url += `&provider_id=${providerId}`;
      }
      this.bookButtonTarget.href = url;
    }
  }

  async refreshTopReviews(siteSlug, serviceId) {
    if (!this.hasTopReviewsTarget || !siteSlug) return;
    
    try {
      const response = await fetch(`/${siteSlug}/top_reviews?service_id=${serviceId}`);
      if (response.ok) {
        const html = await response.text();
        this.topReviewsTarget.innerHTML = html;
      }
    } catch (e) {
      console.error("Failed to refresh top reviews:", e);
    }
  }
}
